// src/pages/PromoIncrementCycleSetup.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * PromoIncrementCycleSetup.jsx
 * Enterprise Hybrid (Zoho + Keka style)
 *
 * Features:
 * - Poppins + Roboto fonts
 * - Create / Edit / Activate / Close cycles (Promotion + Increment)
 * - Eligibility engine (rating, tenure months, attendance %), tailored for INR tax/currency context (display only)
 * - Salary increment matrix by band
 * - Promotion mapping (role -> next role)
 * - Stepwise approval workflow with drag & drop reordering
 * - Preview eligible employees (mock data included)
 * - Import / Export cycles JSON
 * - Save to localStorage (key: hr_cycles_v2)
 * - Modals are compact and positioned not to overlay header (top padding + max-h)
 *
 * Note: Replace mockEmployees with your real API later. Do NOT evaluate user-entered expressions on client in production.
 */

// ----------------- Helpers -----------------
const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap');`;
const LS_KEY = "hr_cycles_v2";
const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 9999)}`;

const DEFAULT_CYCLES = [
  {
    id: uid("cycle"),
    name: "FY26 Annual Promo & Increment",
    type: "Both", // 'Promotion' | 'Increment' | 'Both'
    status: "Draft", // Draft | Active | Closed
    effectiveDate: new Date(new Date().getFullYear(), 3, 1).toISOString().slice(0, 10), // Apr 1
    description: "Annual cycles for promotions and increments (India)",
    eligibility: { minRating: 3.5, minTenureMonths: 12, minAttendancePct: 85 },
    salaryMatrix: [
      { band: "A", minCTC: 0, maxCTC: 600000, percent: 8 },
      { band: "B", minCTC: 600001, maxCTC: 1200000, percent: 7 },
      { band: "C", minCTC: 1200001, maxCTC: 2400000, percent: 6 },
      { band: "D", minCTC: 2400001, maxCTC: 999999999, percent: 5 },
    ],
    promotionMap: [
      { from: "Associate", to: "Senior Associate", bandDelta: 1 },
      { from: "Senior Associate", to: "Lead", bandDelta: 1 },
    ],
    workflow: [
      { id: uid("w"), name: "Self Assessment", role: "Employee" },
      { id: uid("w"), name: "Manager Review", role: "Manager" },
      { id: uid("w"), name: "HR Review", role: "HR" },
      { id: uid("w"), name: "Comp Committee", role: "Compensation" },
    ],
    createdAt: new Date().toISOString(),
    versions: [],
  },
];

// Mock employees (replace with real API later)
const MOCK_EMPLOYEES = [
  { id: "E001", name: "Aman Sharma", role: "Associate", band: "A", ctc: 520000, rating: 4.2, tenureMonths: 14, attendancePct: 92 },
  { id: "E002", name: "Riya Patel", role: "Senior Associate", band: "B", ctc: 850000, rating: 3.1, tenureMonths: 20, attendancePct: 88 },
  { id: "E003", name: "Vikram Rao", role: "Associate", band: "A", ctc: 450000, rating: 3.7, tenureMonths: 8, attendancePct: 95 },
  { id: "E004", name: "Neha Gupta", role: "Lead", band: "C", ctc: 1500000, rating: 4.6, tenureMonths: 44, attendancePct: 98 },
  { id: "E005", name: "Suresh Kumar", role: "Senior Associate", band: "B", ctc: 920000, rating: 4.0, tenureMonths: 36, attendancePct: 80 },
];

// ----------------- Modal (compact; not full-screen) -----------------
const Modal = ({ open, onClose, title, width = "max-w-3xl", children }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
        style={{ background: "rgba(8,8,10,0.35)" }}
      >
        <motion.div
          initial={{ y: -6, scale: 0.995 }}
          animate={{ y: 0, scale: 1 }}
          exit={{ y: -6, scale: 0.995 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[72vh] overflow-auto`}
        >
          <div className="sticky top-0 bg-white z-10 border-b px-5 py-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <div>
              <button onClick={onClose} className="px-3 py-1 rounded hover:bg-slate-100">✕</button>
            </div>
          </div>
          <div className="p-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

// ----------------- Main Component -----------------
export default function PromoIncrementCycleSetup() {
  const [cycles, setCycles] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return DEFAULT_CYCLES;
      return JSON.parse(raw);
    } catch (e) {
      return DEFAULT_CYCLES;
    }
  });

  const [selectedCycleId, setSelectedCycleId] = useState(cycles[0]?.id || null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorDraft, setEditorDraft] = useState(null);
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);

  // persist helper
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(cycles));
  }, [cycles]);

  useEffect(() => {
    if (!selectedCycleId && cycles.length > 0) setSelectedCycleId(cycles[0].id);
  }, [cycles, selectedCycleId]);

  const selectedCycle = useMemo(() => cycles.find((c) => c.id === selectedCycleId) || null, [cycles, selectedCycleId]);

  // --------------- Core Actions ---------------
  const createCycle = (preset = {}) => {
    const c = {
      id: uid("cycle"),
      name: preset.name || `New Cycle ${cycles.length + 1}`,
      type: preset.type || "Both",
      status: "Draft",
      effectiveDate: preset.effectiveDate || new Date().toISOString().slice(0, 10),
      description: preset.description || "",
      eligibility: preset.eligibility || { minRating: 3.5, minTenureMonths: 12, minAttendancePct: 85 },
      salaryMatrix: preset.salaryMatrix || [
        { band: "A", minCTC: 0, maxCTC: 600000, percent: 8 },
        { band: "B", minCTC: 600001, maxCTC: 1200000, percent: 7 },
      ],
      promotionMap: preset.promotionMap || [],
      workflow: preset.workflow || [{ id: uid("w"), name: "Manager Review", role: "Manager" }],
      createdAt: new Date().toISOString(),
      versions: [],
    };
    setCycles((p) => [c, ...p]);
    setSelectedCycleId(c.id);
    setEditorDraft(c);
    setIsEditorOpen(true);
  };

  const saveCycle = (draft) => {
    if (!draft) return;
    setCycles((prev) => {
      const idx = prev.findIndex((x) => x.id === draft.id);
      const updated = { ...draft, updatedAt: new Date().toISOString(), versions: [...(draft.versions || []), { snapshotAt: new Date().toISOString(), snapshot: draft }] };
      if (idx === -1) return [updated, ...prev];
      const copy = [...prev];
      copy[idx] = updated;
      return copy;
    });
    setToast("Cycle saved");
    setTimeout(() => setToast(""), 1600);
    setIsEditorOpen(false);
  };

  const deleteCycle = (id) => {
    if (!window.confirm("Delete cycle? This action is irreversible.")) return;
    setCycles((p) => p.filter((c) => c.id !== id));
    setToast("Cycle deleted");
    setTimeout(() => setToast(""), 1400);
    setSelectedCycleId((prev) => {
      if (prev === id) return cycles[0]?.id || null;
      return prev;
    });
  };

  const changeStatus = (id, status) => {
    setCycles((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    setToast(`Cycle moved to ${status}`);
    setTimeout(() => setToast(""), 1400);
  };

  // --------------- Eligibility Engine ---------------
  const evaluateEligibility = (cycle, employees = MOCK_EMPLOYEES) => {
    if (!cycle) return [];
    const { minRating = 0, minTenureMonths = 0, minAttendancePct = 0 } = cycle.eligibility || {};
    return employees.filter((emp) => {
      const ok = emp.rating >= Number(minRating) && emp.tenureMonths >= Number(minTenureMonths) && emp.attendancePct >= Number(minAttendancePct);
      return ok;
    });
  };

  const eligiblePreview = useMemo(() => evaluateEligibility(selectedCycle, MOCK_EMPLOYEES), [selectedCycle]);

  // --------------- Salary Matrix Calculator ---------------
  const calcIncrement = (cycle, emp) => {
    if (!cycle || !emp) return { percent: 0, increment: 0, newCtc: emp?.ctc || 0 };
    const matrix = cycle.salaryMatrix || [];
    const row = matrix.find((r) => emp.ctc >= (r.minCTC || 0) && emp.ctc <= (r.maxCTC || Infinity));
    const percent = row ? Number(row.percent || 0) : 0;
    const increment = Math.round((emp.ctc * percent) / 100);
    const newCtc = emp.ctc + increment;
    return { percent, increment, newCtc };
  };

  // --------------- Workflow drag & drop ---------------
  const onDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (e, targetIndex) => {
    e.preventDefault();
    const src = Number(e.dataTransfer.getData("text/plain"));
    if (Number.isNaN(src)) return;
    if (!selectedCycle) return;
    const wf = [...selectedCycle.workflow];
    const [moved] = wf.splice(src, 1);
    wf.splice(targetIndex, 0, moved);
    const draft = { ...selectedCycle, workflow: wf };
    setEditorDraft(draft);
    setCycles((prev) => prev.map((c) => (c.id === draft.id ? draft : c)));
  };
  const allowDrop = (e) => e.preventDefault();

  // --------------- Import / Export ---------------
  const exportCycle = (c) => {
    const blob = new Blob([JSON.stringify(c, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${c.name.replace(/\s+/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importCycles = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        const norm = arr.map((x) => ({ ...x, id: x.id || uid("cycle") }));
        setCycles((p) => [...norm, ...p]);
        setToast("Imported cycles");
        setTimeout(() => setToast(""), 1400);
      } catch (err) {
        setToast("Import failed: invalid JSON");
        setTimeout(() => setToast(""), 1800);
      }
    };
    reader.readAsText(file);
  };

  // --------------- UI small helpers ---------------
  const openEditorForCycle = (cycle) => {
    setEditorDraft(JSON.parse(JSON.stringify(cycle)));
    setIsEditorOpen(true);
    setSelectedCycleId(cycle.id);
    // ensure editorDraft is saved to state
  };

  const addWorkflowStepToDraft = () => {
    if (!editorDraft) return;
    const step = { id: uid("w"), name: "New Step", role: "Manager" };
    setEditorDraft({ ...editorDraft, workflow: [...(editorDraft.workflow || []), step] });
  };

  // --------------- Render ---------------
  return (
    <div className="min-h-screen bg-slate-50 p-6" style={{ fontFamily: "Poppins, Roboto, sans-serif" }}>
      <style>{FONT_IMPORT}</style>

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="fixed top-6 right-6 z-50 bg-indigo-600 text-white px-4 py-2 rounded">
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Promo & Increment Cycle Setup</h1>
            <p className="text-sm text-slate-500 mt-1">Create and manage promotion and increment cycles — eligibility, matrix, promotion mapping, and approvals.</p>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              {/* smaller New button per request */}
              <button onClick={() => createCycle()} className="px-3 py-1 rounded bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 text-sm">+ New Cycle</button>
              <label className="px-3 py-1 bg-white border rounded cursor-pointer text-sm">
                Import
                <input ref={fileRef} type="file" accept=".json" onChange={(e) => e.target.files?.[0] && importCycles(e.target.files[0])} className="hidden" />
              </label>
              <button onClick={() => { localStorage.removeItem(LS_KEY); setCycles(DEFAULT_CYCLES); setToast("Reset to defaults"); setTimeout(()=>setToast(""),1400); }} className="px-3 py-1 bg-white border rounded text-sm">Reset</button>
            </div>
            <div className="text-xs text-slate-500">Saved locally (can be hooked to API)</div>
          </div>
        </header>

        {/* cycles list + details */}
        <main className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* left: cycles list */}
          <aside className="lg:col-span-1 bg-white p-4 rounded-2xl shadow-sm h-[60vh] overflow-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-800">Cycles</h3>
              <div className="text-xs text-slate-500">{cycles.length}</div>
            </div>

            <div className="space-y-2">
              {cycles.map((c) => (
                <div
                  key={c.id}
                  className={`p-3 rounded-lg flex items-start gap-3 cursor-pointer transition ${selectedCycleId === c.id ? "ring-2 ring-indigo-300 bg-indigo-50" : "bg-white hover:bg-slate-50"}`}
                >
                  <div style={{ minWidth: 48 }} className="flex-shrink-0">
                    <div className="w-3 h-3 rounded-full mt-1" style={{ background: c.status === "Active" ? "#0ea5a4" : c.status === "Closed" ? "#ef4444" : "#64748b" }} />
                  </div>

                  <div onClick={() => setSelectedCycleId(c.id)} className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 mt-1 truncate">{c.type} · Effective: {c.effectiveDate}</div>
                  </div>

                  <div className="flex flex-col items-end gap-2 ml-2">
                    <button onClick={() => openEditorForCycle(c)} className="text-xs px-2 py-1 rounded bg-white border hover:bg-slate-50">Edit</button>
                    <div className="flex gap-1">
                      <button onClick={() => exportCycle(c)} className="text-xs px-2 py-1 rounded bg-white border hover:bg-slate-50">Exp</button>
                      <button onClick={() => deleteCycle(c.id)} className="text-xs px-2 py-1 rounded bg-rose-50 text-rose-700 border hover:bg-rose-100">Del</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* center: selected cycle summary */}
          <section className="lg:col-span-2 bg-white p-5 rounded-2xl shadow-lg min-h-[60vh] overflow-auto">
            {!selectedCycle ? (
              <div className="text-center text-slate-500 p-8">Select or create a cycle to view details.</div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900">{selectedCycle.name}</h2>
                    <div className="text-sm text-slate-500 mt-1">{selectedCycle.description}</div>
                    <div className="mt-2 text-xs text-slate-500">Status: <span className="font-semibold">{selectedCycle.status}</span> · Effective: {selectedCycle.effectiveDate}</div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="flex gap-2">
                      {selectedCycle.status === "Draft" && <button onClick={() => openEditorForCycle(selectedCycle)} className="px-3 py-2 bg-white border rounded">Open Editor</button>}
                      {selectedCycle.status === "Draft" && <button onClick={() => changeStatus(selectedCycle.id, "Active")} className="px-3 py-2 bg-emerald-600 text-white rounded">Activate</button>}
                      {selectedCycle.status === "Active" && <button onClick={() => changeStatus(selectedCycle.id, "Closed")} className="px-3 py-2 bg-rose-600 text-white rounded">Close</button>}
                    </div>
                    <div className="text-xs text-slate-500">Versions: {selectedCycle.versions?.length || 0}</div>
                  </div>
                </div>

                {/* Analytic cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <div className="text-xs text-slate-500">Eligible (mock)</div>
                    <div className="text-2xl font-bold text-indigo-700">{eligiblePreview.length}</div>
                    <div className="text-xs text-slate-500 mt-1">{MOCK_EMPLOYEES.length} employees scanned</div>
                  </div>
                  <div className="p-4 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500">Avg Rating (eligible)</div>
                    <div className="text-2xl font-bold text-slate-800">{(eligiblePreview.reduce((s, e) => s + e.rating, 0) / Math.max(eligiblePreview.length, 1)).toFixed(2)}</div>
                    <div className="text-xs text-slate-500 mt-1">Preview only</div>
                  </div>
                  <div className="p-4 bg-white rounded-lg border">
                    <div className="text-xs text-slate-500">Est. Total Increment (INR)</div>
                    <div className="text-2xl font-bold text-slate-800">₹ {eligiblePreview.reduce((s, e) => s + calcIncrement(selectedCycle, e).increment, 0).toLocaleString("en-IN")}</div>
                    <div className="text-xs text-slate-500 mt-1">Based on salary matrix</div>
                  </div>
                </div>

                {/* salary matrix + promo map */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold">Salary Increment Matrix</div>
                      <button onClick={() => openEditorForCycle(selectedCycle)} className="text-xs px-2 py-1 rounded bg-white border">Edit Matrix</button>
                    </div>
                    <div className="space-y-2">
                      {(selectedCycle.salaryMatrix || []).map((row, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div>{row.band} &nbsp; (₹{row.minCTC.toLocaleString("en-IN")} - ₹{row.maxCTC.toLocaleString("en-IN")})</div>
                          <div className="font-semibold">{row.percent}%</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold">Promotion Map</div>
                      <button onClick={() => openEditorForCycle(selectedCycle)} className="text-xs px-2 py-1 rounded bg-white border">Edit Map</button>
                    </div>
                    <div className="space-y-2">
                      {(selectedCycle.promotionMap || []).length === 0 && <div className="text-sm text-slate-500">No mappings defined.</div>}
                      {(selectedCycle.promotionMap || []).map((m, idx) => (
                        <div key={idx} className="text-sm flex items-center justify-between">
                          <div>{m.from} → {m.to}</div>
                          <div className="text-xs text-slate-600">Band Δ {m.bandDelta}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* eligible table preview */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold">Eligible Employees (Preview)</h4>
                    <div className="text-xs text-slate-500">Rule: Rating ≥ {selectedCycle?.eligibility?.minRating} · Tenure ≥ {selectedCycle?.eligibility?.minTenureMonths} months</div>
                  </div>
                  <div className="overflow-auto rounded-lg border bg-white">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Employee</th>
                          <th className="px-4 py-2">Role</th>
                          <th className="px-4 py-2">CTC (INR)</th>
                          <th className="px-4 py-2">Rating</th>
                          <th className="px-4 py-2">Tenure (m)</th>
                          <th className="px-4 py-2">Inc %</th>
                          <th className="px-4 py-2">New CTC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligiblePreview.map((emp) => {
                          const calc = calcIncrement(selectedCycle, emp);
                          return (
                            <tr key={emp.id} className="odd:bg-white even:bg-slate-50">
                              <td className="px-4 py-3">{emp.name}</td>
                              <td className="px-4 py-3 text-center">{emp.role}</td>
                              <td className="px-4 py-3 text-right">₹ {emp.ctc.toLocaleString("en-IN")}</td>
                              <td className="px-4 py-3 text-center">{emp.rating}</td>
                              <td className="px-4 py-3 text-center">{emp.tenureMonths}</td>
                              <td className="px-4 py-3 text-right">{calc.percent}%</td>
                              <td className="px-4 py-3 text-right">₹ {calc.newCtc.toLocaleString("en-IN")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* right: workflow / quick actions */}
          <aside className="lg:col-span-1 bg-white p-4 rounded-2xl shadow-sm h-[60vh] overflow-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Workflow</h3>
              <button onClick={() => selectedCycle && openEditorForCycle(selectedCycle)} className="text-xs px-2 py-1 rounded bg-white border">Manage</button>
            </div>

            {selectedCycle?.workflow?.map((w, i) => (
              <div key={w.id} className="p-3 rounded border mb-2 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{i + 1}. {w.name}</div>
                    <div className="text-xs text-slate-500">{w.role}</div>
                  </div>
                  {/* drag handle preview (editor supports reordering) */}
                  <div className="text-xs text-slate-400">drag</div>
                </div>
              </div>
            ))}

            <div className="mt-4">
              <h4 className="text-sm font-semibold mb-2">Quick Actions</h4>
              <button onClick={() => selectedCycle && exportCycle(selectedCycle)} className="w-full px-3 py-2 mb-2 rounded bg-white border">Export Cycle</button>
              <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(selectedCycle || {}, null, 2)); setToast("Copied cycle JSON"); setTimeout(()=>setToast(""),1400); }} className="w-full px-3 py-2 rounded bg-white border">Copy JSON</button>
            </div>
          </aside>
        </main>
      </div>

      {/* --------------- Editor Modal --------------- */}
      <Modal open={isEditorOpen} onClose={() => { setIsEditorOpen(false); setEditorDraft(null); }} title={editorDraft?.name || "Edit Cycle"} width="max-w-4xl">
        {editorDraft ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Cycle Name</label>
                <input value={editorDraft.name} onChange={(e) => setEditorDraft({ ...editorDraft, name: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Type</label>
                <select value={editorDraft.type} onChange={(e) => setEditorDraft({ ...editorDraft, type: e.target.value })} className="w-full px-3 py-2 border rounded-md">
                  <option value="Both">Promotion & Increment</option>
                  <option value="Promotion">Promotion Only</option>
                  <option value="Increment">Increment Only</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-slate-600">Effective Date</label>
                <input type="date" value={editorDraft.effectiveDate} onChange={(e) => setEditorDraft({ ...editorDraft, effectiveDate: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
              </div>

              <div>
                <label className="text-xs text-slate-600">Status</label>
                <select value={editorDraft.status} onChange={(e) => setEditorDraft({ ...editorDraft, status: e.target.value })} className="w-full px-3 py-2 border rounded-md">
                  <option>Draft</option>
                  <option>Active</option>
                  <option>Closed</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-600">Description</label>
              <textarea value={editorDraft.description} onChange={(e) => setEditorDraft({ ...editorDraft, description: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows={3} />
            </div>

            {/* Eligibility */}
            <div className="border p-3 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Eligibility Rules</h4>
                <div className="text-xs text-slate-500">For Indian clients — compare ratings, tenure (months), attendance %</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-600">Min Rating</label>
                  <input type="number" step="0.1" value={editorDraft.eligibility?.minRating || 0} onChange={(e) => setEditorDraft({ ...editorDraft, eligibility: { ...editorDraft.eligibility, minRating: Number(e.target.value) } })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Min Tenure (months)</label>
                  <input type="number" value={editorDraft.eligibility?.minTenureMonths || 0} onChange={(e) => setEditorDraft({ ...editorDraft, eligibility: { ...editorDraft.eligibility, minTenureMonths: Number(e.target.value) } })} className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Min Attendance %</label>
                  <input type="number" value={editorDraft.eligibility?.minAttendancePct || 0} onChange={(e) => setEditorDraft({ ...editorDraft, eligibility: { ...editorDraft.eligibility, minAttendancePct: Number(e.target.value) } })} className="w-full px-3 py-2 border rounded-md" />
                </div>
              </div>
            </div>

            {/* Salary Matrix editor */}
            <div className="border p-3 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Salary Increment Matrix (band → %)</h4>
                <button onClick={() => setEditorDraft({ ...editorDraft, salaryMatrix: [...(editorDraft.salaryMatrix || []), { band: "NEW", minCTC: 0, maxCTC: 999999999, percent: 5 }] })} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white">+ Add Row</button>
              </div>

              <div className="space-y-2">
                {(editorDraft.salaryMatrix || []).map((r, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center">
                    <input value={r.band} onChange={(e) => {
                      const m = [...editorDraft.salaryMatrix]; m[idx].band = e.target.value; setEditorDraft({ ...editorDraft, salaryMatrix: m });
                    }} className="px-2 py-2 border rounded-md" />
                    <input type="number" value={r.minCTC} onChange={(e) => { const m = [...editorDraft.salaryMatrix]; m[idx].minCTC = Number(e.target.value); setEditorDraft({ ...editorDraft, salaryMatrix: m }); }} placeholder="minCTC" className="px-2 py-2 border rounded-md" />
                    <input type="number" value={r.maxCTC} onChange={(e) => { const m = [...editorDraft.salaryMatrix]; m[idx].maxCTC = Number(e.target.value); setEditorDraft({ ...editorDraft, salaryMatrix: m }); }} placeholder="maxCTC" className="px-2 py-2 border rounded-md" />
                    <input type="number" value={r.percent} onChange={(e) => { const m = [...editorDraft.salaryMatrix]; m[idx].percent = Number(e.target.value); setEditorDraft({ ...editorDraft, salaryMatrix: m }); }} placeholder="percent" className="px-2 py-2 border rounded-md" />
                    <div className="flex gap-1">
                      <button onClick={() => { const m = [...editorDraft.salaryMatrix]; m.splice(idx, 1); setEditorDraft({ ...editorDraft, salaryMatrix: m }); }} className="px-2 py-2 bg-rose-50 text-rose-600 rounded">Del</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Promotion map editor */}
            <div className="border p-3 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Promotion Map</h4>
                <button onClick={() => setEditorDraft({ ...editorDraft, promotionMap: [...(editorDraft.promotionMap || []), { from: "Role", to: "Next Role", bandDelta: 1 }] })} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white">+ Add Mapping</button>
              </div>

              <div className="space-y-2">
                {(editorDraft.promotionMap || []).map((m, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input value={m.from} onChange={(e) => { const arr = [...editorDraft.promotionMap]; arr[idx].from = e.target.value; setEditorDraft({ ...editorDraft, promotionMap: arr }); }} className="px-2 py-2 border rounded-md" />
                    <div className="text-sm">→</div>
                    <input value={m.to} onChange={(e) => { const arr = [...editorDraft.promotionMap]; arr[idx].to = e.target.value; setEditorDraft({ ...editorDraft, promotionMap: arr }); }} className="px-2 py-2 border rounded-md" />
                    <input type="number" value={m.bandDelta} onChange={(e) => { const arr = [...editorDraft.promotionMap]; arr[idx].bandDelta = Number(e.target.value); setEditorDraft({ ...editorDraft, promotionMap: arr }); }} className="w-20 px-2 py-2 border rounded-md" />
                    <button onClick={() => { const arr = [...editorDraft.promotionMap]; arr.splice(idx, 1); setEditorDraft({ ...editorDraft, promotionMap: arr }); }} className="px-2 py-2 bg-rose-50 text-rose-600 rounded">Del</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Workflow builder (simple DnD) */}
            <div className="border p-3 rounded-md">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Approval Workflow</h4>
                <div>
                  <button onClick={() => addWorkflowStepToDraft()} className="px-2 py-1 rounded bg-emerald-600 text-white text-xs">+ Step</button>
                </div>
              </div>
              <div className="mt-2 space-y-2">
                {(editorDraft.workflow || []).map((w, idx) => (
                  <div key={w.id} draggable onDragStart={(e) => onDragStart(e, idx)} onDragOver={allowDrop} onDrop={(e) => onDrop(e, idx)} className="p-3 rounded border bg-white flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-indigo-50 flex items-center justify-center text-indigo-700 font-semibold">{idx + 1}</div>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <input value={w.name} onChange={(e) => {
                        const wf = [...editorDraft.workflow]; wf[idx].name = e.target.value; setEditorDraft({ ...editorDraft, workflow: wf });
                      }} className="px-2 py-1 border rounded" />
                      <input value={w.role} onChange={(e) => {
                        const wf = [...editorDraft.workflow]; wf[idx].role = e.target.value; setEditorDraft({ ...editorDraft, workflow: wf });
                      }} className="px-2 py-1 border rounded" />
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => {
                        const wf = [...editorDraft.workflow]; wf.splice(idx, 1); setEditorDraft({ ...editorDraft, workflow: wf });
                      }} className="px-2 py-1 rounded bg-rose-50 text-rose-600">Del</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button onClick={() => { setIsEditorOpen(false); setEditorDraft(null); }} className="px-4 py-2 rounded bg-white border">Cancel</button>
              <button onClick={() => saveCycle(editorDraft)} className="px-4 py-2 rounded bg-indigo-600 text-white">Save Cycle</button>
            </div>
          </div>
        ) : (
          <div className="text-center text-slate-500 p-6">Loading...</div>
        )}
      </Modal>
    </div>
  );
}
